import React, { FC, useEffect, useState } from "react";
import { translate } from "../../../core";
import { Button, Checkbox, Col, Form, Input, Modal, Row, Space } from "antd";
import { Combo, StimulsoftViewer } from "../../../core/component";
import { InventoryByPatternRequest, ItemType } from "../type";
import { getItemTypeComboOptions } from "../func/type-options";
import { Contact } from "../../contact/type/contact";
import { vendorOptionLabel } from "../../contact/component/vendorLabel.util";
import { useLazyQuery } from "@apollo/client";
import { QueryPageableRequest, QueryPageableResponse, } from "../../../core/type";
import { ContactFindRequest } from "../../contact/type/contact.query";
import { GetVendorsForLookup } from "../../contact/graphql/contact.queries.graphql";
import { ItemSearchCategoryTree } from "./ItemSearchCategoryTree";
import { showItemOptionsMapper, ShowItemOptionsSelect } from '../func/showItemOptionsList'
import { ItemShowOptions } from '../type/item-query'

type InventoryByPatternRequestForm = Omit<
  InventoryByPatternRequest,
  "categoryId" | "subcategoryId" | "subcategory2Id" | 'archived'
> & {
  category?: Pick<
    InventoryByPatternRequest,
    "categoryId" | "subcategoryId" | "subcategory2Id"
  >
  status: ItemShowOptions
};

export const InventoryByPatternReport: FC = () => {
  const [form] = Form.useForm<InventoryByPatternRequestForm>();
  const [request, setRequest] = useState<InventoryByPatternRequest>();
  const [findContacts] = useLazyQuery<
    QueryPageableResponse<Contact, "contactsFind">,
    QueryPageableRequest<ContactFindRequest>
  >(GetVendorsForLookup);

  const handleSubmit = (data: InventoryByPatternRequestForm) => {
    const { category } = data;
    delete data.category;
    setRequest({
      ...data,
      categoryId: category?.categoryId ?? [],
      subcategoryId: category?.subcategoryId ?? [],
      subcategory2Id: category?.subcategory2Id ?? [],
      archived: showItemOptionsMapper[data.status ?? ItemShowOptions.All].value,
    });
  };
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);

  const isFormValid = async (): Promise<boolean> => {
    try {
      await form.validateFields({ validateOnly: true });
      return true;
    } catch {
      return false;
    }
  };

  const handleOK = async () => {
    if (await isFormValid()) {
      setConfirmModalVisible(false);
    }
    form.submit();
  };

  return (
    <>
      <Button
        type="primary"
        onClick={() => setConfirmModalVisible(true)}
        className="btn-primary-gradient"
      >
        {translate("new_report")}
      </Button>

      <StimulsoftViewer
        name="inventory-by-pattern"
        request={request}
        skip={!request}
      />

      <Modal
        open={confirmModalVisible}
        onCancel={() => setConfirmModalVisible(false)}
        width='40%'
        footer={<Space>
          <Button
            onClick={() => setConfirmModalVisible(false)}
          >
            {translate('cancel')}
          </Button>

          <Button
            onClick={() => form.resetFields()}
          >
            {translate('clear')}
          </Button>

          <Button
            type='primary'
            onClick={handleOK}
          >
            {translate('get_report')}
          </Button>
        </Space>}
      >
        <Form
          initialValues={{ inStock: true, status: ItemShowOptions.NoneArchived }}
          layout="vertical"
          form={form}
          onFinish={handleSubmit}
          className="d-flex flex-column gap-2 mt-4"
        >
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item name="types" label={translate("type")}>
                <Combo<any, ItemType>
                  allowMultiple
                  size="middle"
                  options={getItemTypeComboOptions()}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={translate("vendor")} name="vendorId">
                <Combo<Contact, number>
                  size="middle"
                  fetch={async ({ request, value, search }) => {
                    const { data } = await findContacts({
                      variables: {
                        request: {
                          ...request,
                          take: request?.take ?? 100,
                          skip: request?.skip ?? 0,
                          searchVendor: search,
                          id: value,
                        },
                      },
                    });

                    const list = data?.contactsFind.data ?? [];

                    return list.map((e) => ({
                      value: e.id || 0,
                      display: vendorOptionLabel(e),
                      item: e || {},
                    }));
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item name="style" label={translate("style", "/", "pattern")}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="category" label={translate("category")}>
                <ItemSearchCategoryTree />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item
                name='status'
                label={translate('show')}
              >
                <ShowItemOptionsSelect/>
              </Form.Item>
            </Col>
            <Col span={12} className='d-flex align-items-center'>
              <Space direction='vertical'>
                <Form.Item name="inStock" valuePropName="checked" noStyle>
                  <Checkbox>{translate("in_stock_only")}</Checkbox>
                </Form.Item>

                <Form.Item name="showPictures" valuePropName="checked" noStyle>
                  <Checkbox>{translate("show_pictures")}</Checkbox>
                </Form.Item>
              </Space>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};
